/**
 * Pure helpers for the admin Ask the Jaguars monitor (page.tsx). Kept
 * dependency-free so the outcome/tone mapping — and the reply-count rollup —
 * are covered directly by ask.test.ts rather than only indirectly through a
 * component test.
 *
 * Mirrors the state business_requests can actually be in (backend/supabase/
 * migrations/20260923120300_jaguar_owned_ask.sql):
 *   - status is 'open' | 'closed' | 'removed'.
 *   - outcome is null until record_request_outcome() runs, then one of
 *     'chose_business' | 'someone_else' | 'still_looking' | 'no_longer_needed'.
 *   - 'still_looking' is the one outcome that does NOT close the request via
 *     record_request_outcome() itself (status stays 'open' — see the RPC's
 *     `status = case when p_outcome = 'still_looking' then 'open' else
 *     'closed' end`). BUT the requester can separately self-close their own
 *     OPEN request directly (the before-update trigger's self-close branch),
 *     which leaves a previously-recorded outcome untouched — so a row CAN
 *     end up with status='closed' AND outcome='still_looking'. Once that
 *     happens the request is genuinely closed; showing "Still looking" would
 *     be stale. outcomeLabel/outcomeTone therefore key 'still_looking' off
 *     status too: open+still_looking → "Still looking" (gold, still live),
 *     closed+still_looking → "Closed" (neutral, same as any other closed
 *     request with no outcome fit for that status).
 *   - an admin can only ever move a request to 'removed' (the update guard),
 *     so 'removed' takes priority over whatever outcome happened to be set —
 *     including a 'chose_business' or other outcome recorded before removal.
 */

import { serviceAreaLabel } from '../businesses/decision'
import type { Tone } from '../../theme/listkit'

export type RequestStatus = 'open' | 'closed' | 'removed'
export type RequestOutcome =
  | 'chose_business'
  | 'someone_else'
  | 'still_looking'
  | 'no_longer_needed'

/**
 * The wider, caller-facing type: the DB column is plain `text` (nullable),
 * so page.tsx's BusinessRequest#outcome is typed `string | null` — narrower
 * than that would fight the data it actually reads. These helpers accept the
 * wide type and fall through to the raw value for anything unrecognized.
 */
export type OutcomeInput = RequestOutcome | string | null | undefined

/** Human label for a request's outcome/status pair. */
export function outcomeLabel(outcome: OutcomeInput, status: string): string {
  if (status === 'removed') return 'Removed'
  if (outcome === 'still_looking') return status === 'open' ? 'Still looking' : 'Closed'
  switch (outcome) {
    case 'chose_business':
      return 'Chose a business'
    case 'someone_else':
      return 'Chose someone else'
    case 'no_longer_needed':
      return 'No longer needed'
    default:
      break
  }
  if (status === 'open') return 'Open'
  if (status === 'closed') return 'Closed'
  return outcome ?? status
}

/** Pill tone per outcome/status pair (listkit Tone). */
export function outcomeTone(outcome: OutcomeInput, status: string): Tone {
  if (status === 'removed') return 'red'
  if (outcome === 'chose_business') return 'green'
  if (outcome === 'still_looking') return status === 'open' ? 'gold' : 'neutral'
  if (status === 'open' && !outcome) return 'gold'
  return 'neutral'
}

/**
 * Human label per service-area slug — the SAME four values as the businesses
 * `service_areas` CHECK constraint and business_requests.area's own CHECK
 * ('miami-dade','broward','palm-beach','anywhere'), so this is a re-export of
 * businesses/decision.ts's serviceAreaLabel rather than a second copy of the
 * same switch.
 */
export const areaLabel = serviceAreaLabel

/** Reply count per request id, from a flat list of {request_id} rows. */
export function countReplies(replies: { request_id: string }[]): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const r of replies) {
    counts[r.request_id] = (counts[r.request_id] ?? 0) + 1
  }
  return counts
}
